import {FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  useGraphqlLazyQuery,
  useGraphqlMutation,
  TriggerResult
} from "../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  setEntitlementStatus as setEntitlementStatusMutation
} from "../../../amplify/graphql";
import {InterstateOnChangeCallback} from "@interstate/components/InterstateEvents";
import {CheckBox, CheckBoxEventValue} from "@interstate/components/CheckBox";
import {Endpoints} from "../../../runtime";
import {ModalWithAlert, useAlert} from "../../../alerting";
import {OnCompleteCallback} from "../../../utils";
import {useOperationalEntity} from "../../context";
import {
  LookupOperationalEntity,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  SetEntitlementStatus,
  assertOperationalEntityPresent,
  SetEntitlementStatusData,
  SetEntitlementStatusVariables
} from "../../backend";
import {EnterpriseEntitlementV2} from "@common-core/coat-operational-hierarchy-appsync-model";
import {isEntitlementActive} from "../../util/isEntitlementActive";

export const EditEnterpriseEntitlementsModal: FC<{
  id: string;
  initialEntitlements: EnterpriseEntitlementV2[];
  onComplete: OnCompleteCallback;
}> = ({id, initialEntitlements, onComplete}) => {
  const {t} = useTranslation();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [working, setWorking] = useState(false);
  const [entitlements, setEntitlements] = useState(initialEntitlements);
  const {entity, setEntity} = useOperationalEntity();

  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    document: LookupOperationalEntity,
    amplifyQuery: lookupOperationalEntityQuery,
    context: {endpoint: Endpoints.APPSYNC},
    fetchPolicy: "network-only"
  });

  const [setEntitlementStatus] = useGraphqlMutation<
    SetEntitlementStatusData,
    SetEntitlementStatusVariables
  >({
    document: SetEntitlementStatus,
    amplifyMutation: setEntitlementStatusMutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const refreshEntity = async (): Promise<void> => {
    return lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const entitlementChanged: InterstateOnChangeCallback<
    CheckBoxEventValue
  > = event => {
    setEntitlements(current => {
      const index = current.findIndex(
        entitlement =>
          entitlement.featureId === event.target.value.checkboxValue
      );
      return [
        ...current.slice(0, index),
        {
          ...current[index],
          status: event.target.value.checked ? "active" : "inactive"
        },
        ...current.slice(index + 1)
      ];
    });
  };

  const setEntitlementsStatus = async (
    entitlements: EnterpriseEntitlementV2[]
  ): Promise<
    PromiseSettledResult<TriggerResult<SetEntitlementStatusData>>[]
  > => {
    return await Promise.allSettled(
      entitlements
        .filter(
          (entitlement, index) =>
            isEntitlementActive(entitlement) !==
            isEntitlementActive(initialEntitlements[index])
        )
        .map(entitlement =>
          setEntitlementStatus({
            variables: {
              enterpriseEntitlement: {
                entityId: id,
                entityType: "OE",
                featureId: entitlement.featureId,
                status: entitlement.status
              }
            }
          })
        )
    );
  };

  // TODO: [TD] Improve the failure experience for our users; collect and display all errors
  //  to highlight the successful vs failed updates
  const assertAllResultsSuccessful = (
    results: PromiseSettledResult<TriggerResult<SetEntitlementStatusData>>[]
  ) => {
    results.forEach(result => {
      if (
        result.status === "fulfilled" &&
        result.value.data?.entity.success === false
      ) {
        throw new Error(t("common-errors.backend-error"));
      }
      if (result.status === "rejected") {
        throw new Error(
          result.reason?.message || t("common-errors.backend-error")
        );
      }
    });
  };

  const saveChanges = async () => {
    setWorking(true);
    clearAlert();
    setEntitlementsStatus(entitlements)
      .then(assertAllResultsSuccessful)
      .then(refreshEntity)
      .then(() => onComplete(true))
      .catch(createErrorAlert)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };
  return (
    <ModalWithAlert
      id={"edit-enterprise-entitlements-modal"}
      data-testid={"edit-enterprise-entitlements-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-enterprise-entitlements")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      {entitlements.map(entitlement => (
        <CheckBox
          key={entitlement.featureId}
          id={`checkbox-${entitlement.featureId}`}
          data-testid={`checkbox-${entitlement.featureId}`}
          value={entitlement.featureId}
          onChange={entitlementChanged}
          label={entitlement.featureName}
          checked={isEntitlementActive(entitlement)}
        />
      ))}
    </ModalWithAlert>
  );
};
